// src/pages/ceed/CeedProfilePage.jsx
import { useEffect, useRef, useState } from "react";
import {
  FaCamera, FaCheck, FaEye, FaEyeSlash,
} from "react-icons/fa6";
import { MdDelete } from "react-icons/md";
import { ImCross } from "react-icons/im";
import { HiPhoto } from "react-icons/hi2";
import { TbPhotoDown } from "react-icons/tb";
import { IoIosCheckbox, IoIosWarning } from "react-icons/io";
import { RiInformationFill } from "react-icons/ri";
import { BiRefresh } from "react-icons/bi";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/material.css";
import { useToast } from "../../components/ToastProvider";

const AVATAR_VIEW = 320;
const OUT_W = 150;
const OUT_H = 150;

/* ── Style injection ──────────────────────────────────────────── */
const STYLE_ID = "ceed-profile-styles";
function injectStyles() {
  if (document.getElementById(STYLE_ID)) return;
  const el = document.createElement("style");
  el.id = STYLE_ID;
  el.textContent = `
    .cp-wrap { font-family: 'DM Sans', sans-serif; color: #0c1118; }

    /* Page header */
    .cp-page-title {
      font-family: 'DM Sans', sans-serif;
      font-size: 24px; font-weight: 700; color: #0c1118; line-height: 1.2;
    }
    .cp-page-sub {
      font-family: 'DM Sans', sans-serif;
      font-size: 13px; color: #6b7280; margin-top: 4px;
    }

    /* Layout */
    .cp-layout {
      display: grid; grid-template-columns: 300px 1fr;
      gap: 20px; align-items: start; margin-top: 24px;
    }
    @media (max-width: 1023px) { .cp-layout { grid-template-columns: 1fr; } }

    /* Cards */
    .cp-card {
      background: #fff; border: 1px solid #e8ecf0; border-radius: 10px;
      overflow: hidden;
    }
    .cp-card-head {
      padding: 18px 20px 14px; border-bottom: 1px solid #f3f4f6;
      display: flex; align-items: center; justify-content: space-between;
    }
    .cp-card-title { font-size: 14px; font-weight: 600; color: #0c1118; }
    .cp-card-sub { font-size: 12px; color: #9ca3af; margin-top: 2px; }
    .cp-card-body { padding: 20px; }

    /* Section label */
    .cp-section-label {
      font-family: 'DM Sans', sans-serif;
      font-size: 9.5px; letter-spacing: .04em;
      color: #9ca3af; margin-bottom: 6px; display: block;
    }

    /* Form grid */
    .cp-form-grid {
      display: grid; grid-template-columns: 1fr 1fr; gap: 16px;
    }
    @media (max-width: 767px) { .cp-form-grid { grid-template-columns: 1fr; } }
    .cp-form-grid-3 {
      display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 16px;
    }
    @media (max-width: 1199px) { .cp-form-grid-3 { grid-template-columns: 1fr; } }

    /* Form field */
    .cp-field { display: flex; flex-direction: column; gap: 6px; }
    .cp-field-label {
      font-family: 'DM Sans', sans-serif;
      font-size: 10px; letter-spacing: .04em;
      color: #6b7280; display: flex; align-items: center; gap: 4px;
    }
    .cp-field-req { color: #111111; }
    .cp-input {
      padding: 9px 12px; border: 1px solid #e2e8f0; border-radius: 7px;
      font-size: 14px; color: #0c1118; background: #fff; outline: none;
      transition: border-color 150ms, box-shadow 150ms;
      font-family: 'DM Sans', sans-serif; width: 100%; box-sizing: border-box;
    }
    .cp-input:focus {
      border-color: #111111;
      box-shadow: 0 0 0 3px rgba(0,0,0,0.08);
    }
    .cp-input-wrap { position: relative; }
    .cp-input-eye {
      position: absolute; right: 10px; top: 50%; transform: translateY(-50%);
      background: none; border: none; cursor: pointer; color: #9ca3af;
      display: flex; align-items: center; padding: 2px;
      transition: color 150ms;
    }
    .cp-input-eye:hover { color: #374151; }
    .cp-input-gen {
      position: absolute; right: 34px; top: 50%; transform: translateY(-50%);
      background: none; border: none; cursor: pointer;
      color: #111111; display: flex; align-items: center; padding: 2px;
      transition: color 150ms;
    }
    .cp-input-gen:hover { color: #b45309; }

    /* Password strength bar */
    .cp-pw-bar-wrap {
      height: 4px; background: #f3f4f6; border-radius: 2px; margin-top: 8px; overflow: hidden;
    }
    .cp-pw-bar { height: 100%; border-radius: 2px; transition: width .3s, background .3s; }

    /* Alert banners */
    .cp-alert {
      display: flex; align-items: flex-start; gap: 10px;
      padding: 12px 14px; border-radius: 7px; border: 1px solid;
      margin-bottom: 16px; font-size: 13px;
    }
    .cp-alert-success { background: rgba(16,185,129,.07); border-color: rgba(16,185,129,.25); color: #065f46; }
    .cp-alert-error   { background: rgba(239,68,68,.07);  border-color: rgba(239,68,68,.25);  color: #991b1b; }
    .cp-alert-info    { background: rgba(59,130,246,.07); border-color: rgba(59,130,246,.2);  color: #1e40af; }
    .cp-alert-close {
      margin-left: auto; background: none; border: none; cursor: pointer;
      font-size: 18px; line-height: 1; opacity: .5; flex-shrink: 0;
      transition: opacity 150ms;
    }
    .cp-alert-close:hover { opacity: 1; }

    /* Email verify banner */
    .cp-email-banner {
      margin-top: 14px; padding: 14px;
      background: rgba(0,0,0,0.04); border: 1px solid rgba(0,0,0,0.12);
      border-radius: 8px;
    }
    .cp-email-banner-title { font-size: 13px; font-weight: 600; color: #92400e; }
    .cp-email-banner-body  { font-size: 12.5px; color: #78350f; margin-top: 4px; }
    .cp-email-banner-btns  { display: flex; gap: 8px; margin-top: 10px; flex-wrap: wrap; }

    /* Buttons */
    .cp-btn {
      display: inline-flex; align-items: center; gap: 7px;
      padding: 9px 18px; border-radius: 7px; font-size: 13px; font-weight: 500;
      cursor: pointer; border: none; transition: background 150ms;
      font-family: 'DM Sans', sans-serif;
    }
    .cp-btn-dark { background: #0c1118; color: #fff; }
    .cp-btn-dark:hover { background: #1e2832; }
    .cp-btn-dark:disabled { opacity: .5; cursor: default; }
    .cp-btn-outline { background: #fff; color: #374151; border: 1px solid #e2e8f0; }
    .cp-btn-outline:hover { background: #f9fafb; }
    .cp-btn-outline:disabled { opacity: .5; cursor: default; }
    .cp-btn-amber { background: rgba(0,0,0,0.06); color: #333333; }
    .cp-btn-amber:hover { background: rgba(0,0,0,0.10); }
    .cp-btn-sm { padding: 6px 12px; font-size: 12px; }

    /* Spinner */
    .cp-spinner {
      width: 14px; height: 14px; border: 2px solid currentColor;
      border-top-color: transparent; border-radius: 50%;
      animation: cpSpin .7s linear infinite; flex-shrink: 0;
    }
    @keyframes cpSpin { to { transform: rotate(360deg); } }

    /* Avatar section */
    .cp-avatar-display { display: flex; align-items: center; gap: 16px; margin-bottom: 20px; }
    .cp-avatar-img {
      width: 80px; height: 80px; border-radius: 50%;
      object-fit: cover; border: 3px solid #e8ecf0;
      position: relative;
    }
    .cp-avatar-check {
      position: absolute; top: -4px; right: -4px;
      width: 22px; height: 22px; border-radius: 50%;
      background: #10b981; border: 2px solid #fff;
      display: flex; align-items: center; justify-content: center;
      color: #fff;
    }
    .cp-avatar-name { font-size: 15px; font-weight: 600; color: #0c1118; }
    .cp-avatar-role {
      font-family: 'DM Sans', sans-serif;
      font-size: 10px; letter-spacing: .1em; color: #111111; margin-top: 2px;
    }
    .cp-avatar-status { font-size: 12px; color: #9ca3af; margin-top: 2px; }
    .cp-avatar-actions { display: flex; gap: 8px; margin-top: 10px; flex-wrap: wrap; }

    /* Drop zone */
    .cp-drop-zone {
      border: 2px dashed #e2e8f0; border-radius: 8px; padding: 24px;
      text-align: center; cursor: pointer; transition: all 160ms;
    }
    .cp-drop-zone:hover, .cp-drop-zone.drag-active {
      border-color: #111111; background: rgba(0,0,0,0.02);
    }
    .cp-drop-title { font-size: 13px; font-weight: 500; color: #374151; margin-top: 8px; }
    .cp-drop-sub   { font-size: 11.5px; color: #9ca3af; margin-top: 3px; }

    /* Crop editor */
    .cp-crop-wrap {
      display: flex; justify-content: center; margin-bottom: 16px;
    }
    .cp-crop-preview {
      text-align: center;
    }
    .cp-crop-preview-label { font-size: 12px; color: #6b7280; margin-bottom: 6px; }
    .cp-crop-preview-circle {
      width: 72px; height: 72px; border-radius: 50%; overflow: hidden;
      border: 3px solid #e8ecf0; margin: 0 auto;
    }

    /* Loading page */
    .cp-loading-page {
      display: flex; align-items: center; justify-content: center;
      min-height: 60vh; gap: 12px; font-size: 13px; color: #9ca3af;
    }
    .cp-loading-spinner {
      width: 24px; height: 24px; border: 2px solid #e8ecf0;
      border-top-color: #0c1118; border-radius: 50%;
      animation: cpSpin .7s linear infinite;
    }

    /* Status indicator */
    .cp-status-dot { width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; }
    .cp-status-row { display: flex; align-items: center; gap: 6px; font-size: 12px; }
  `;
  document.head.appendChild(el);
}

/* ── Sub-components ───────────────────────────────────────────── */
function Alert({ message, onClose }) {
  if (!message) return null;
  const cls = message.type === "success" ? "cp-alert-success"
    : message.type === "error" ? "cp-alert-error" : "cp-alert-info";
  return (
    <div className={`cp-alert ${cls}`}>
      <span style={{ flexShrink: 0, marginTop: 1 }}>
        {message.type === "success" ? <IoIosCheckbox /> : message.type === "error" ? <ImCross /> : <RiInformationFill />}
      </span>
      <span>{message.text}</span>
      <button className="cp-alert-close" onClick={onClose}>×</button>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="cp-field">
      <label className="cp-field-label">
        {label}
        {required && <span className="cp-field-req">*</span>}
      </label>
      {children}
    </div>
  );
}

/* ── Main component ───────────────────────────────────────────── */
export default function CeedProfilePage() {
  useEffect(() => { injectStyles(); }, []);

  const [form, setForm] = useState({
    firstname: "", lastname: "", country: "", birthday: "",
    phone: "", phoneCountryCode: "", email: "",
  });
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [pendingEmail, setPendingEmail] = useState(null);
  const [msg, setMsg] = useState(null);
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [pwMsg, setPwMsg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPasswords, setShowPasswords] = useState({ current: false, next: false, confirm: false });

  const backendBase = process.env.REACT_APP_BACKEND_URL;
  const toast = useToast();

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await fetch(`${backendBase}/api/profile/me`, { credentials: "include", cache: "no-store" });
        if (r.ok) {
          const d = await r.json();
          setForm({
            firstname: d.user.firstname || "",
            lastname: d.user.lastname || "",
            country: d.user.country || "",
            birthday: d.user.birthday ? String(d.user.birthday).slice(0, 10) : "",
            phone: d.user.phone || "",
            phoneCountryCode: "",
            email: d.user.email || "",
          });
          setAvatarUrl(d.user.avatarUrl || null);
          setPendingEmail(d.user.pendingEmail || null);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [backendBase]);

  const handlePhoneChange = (value, country) => {
    setForm((prev) => ({ ...prev, phone: value, phoneCountryCode: country.countryCode || "" }));
  };

  async function save() {
    setMsg(null);
    setSaving(true);
    try {
      const r = await fetch(`${backendBase}/api/profile/me`, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, phonenumber: form.phone, phoneIso2: form.phoneCountryCode.toUpperCase() }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setMsg({ type: "error", text: d?.message || "Save failed" }); return; }
      setMsg({ type: "success", text: "Profile updated successfully!" });
      window.dispatchEvent(new CustomEvent("profile:updated", { detail: { firstname: d.user?.firstname, lastname: d.user?.lastname, email: d.user?.email } }));
      setPendingEmail(d.user?.pendingEmail || null);
      if (d.requiresEmailVerify) {
        setMsg({ type: "info", text: "We sent a verification code to your new email address. Please check your inbox to complete the change." });
      }
    } finally {
      setSaving(false);
    }
  }

  async function confirmEmailChange() {
    const code = prompt("Enter the 6-digit code sent to your new email:");
    if (!code) return;
    try {
      const r = await fetch(`${backendBase}/api/profile/confirm-email`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code }),
      });
      if (r.ok) { setMsg({ type: "success", text: "Your email has been updated and verified successfully!" }); setPendingEmail(null); }
      else { const d = await r.json().catch(() => ({})); toast.error(d?.message || "Verification failed"); }
    } catch { toast.error("Verification failed. Please try again."); }
  }

  async function resendEmailCode() {
    try {
      const r = await fetch(`${backendBase}/api/profile/resend-email-code`, { method: "POST", credentials: "include" });
      if (r.ok) setMsg({ type: "success", text: "Verification code resent to your email." });
    } catch { setMsg({ type: "error", text: "Failed to resend code. Please try again." }); }
  }

  const generatePassword = () => {
    const lower = "abcdefghijklmnopqrstuvwxyz", upper = "ABCDEFGHIJKLMNOPQRSTUVWXYZ", nums = "0123456789", syms = "!@#$%^&*()_+-=[]{}|;:,.<>?";
    const all = lower + upper + nums + syms;
    let p = lower[Math.floor(Math.random()*lower.length)] + upper[Math.floor(Math.random()*upper.length)] + nums[Math.floor(Math.random()*nums.length)] + syms[Math.floor(Math.random()*syms.length)];
    for (let i = 4; i < 12; i++) p += all[Math.floor(Math.random()*all.length)];
    p = p.split("").sort(() => Math.random() - 0.5).join("");
    setPw((prev) => ({ ...prev, next: p }));
  };

  const getPasswordStrength = (password) => {
    if (!password) return { strength: 0, label: "No password", color: "#e5e7eb", pct: 0 };
    const checks = { length: password.length >= 8, lower: /[a-z]/.test(password), upper: /[A-Z]/.test(password), nums: /\d/.test(password), syms: /[^a-zA-Z0-9]/.test(password), long: password.length >= 12 };
    const score = Object.values(checks).filter(Boolean).length;
    if (score <= 2) return { strength: 1, label: "Weak",   color: "#ef4444", pct: 25 };
    if (score <= 4) return { strength: 2, label: "Fair",   color: "#f59e0b", pct: 50 };
    if (score <= 5) return { strength: 3, label: "Good",   color: "#3b82f6", pct: 75 };
    return              { strength: 4, label: "Strong", color: "#10b981", pct: 100 };
  };

  const passwordStrength = getPasswordStrength(pw.next);

  const togglePasswordVisibility = (field) =>
    setShowPasswords((prev) => ({ ...prev, [field]: !prev[field] }));

  async function changePassword() {
    setPwMsg(null);
    if (pw.next !== pw.confirm) { setPwMsg({ type: "error", text: "New passwords do not match" }); return; }
    if (pw.next.length < 8) { setPwMsg({ type: "error", text: "Password must be at least 8 characters long" }); return; }
    try {
      const r = await fetch(`${backendBase}/api/profile/change-password`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ currentPassword: pw.current, newPassword: pw.next }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setPwMsg({ type: "error", text: d?.message || "Password change failed" }); }
      else { setPwMsg({ type: "success", text: "Password updated successfully!" }); setPw({ current: "", next: "", confirm: "" }); setShowPasswords({ current: false, next: false, confirm: false }); }
    } catch { setPwMsg({ type: "error", text: "Password change failed. Please try again." }); }
  }

  if (loading) {
    return (
      <div className="cp-loading-page">
        <div className="cp-loading-spinner" />
        Loading your profile…
      </div>
    );
  }

  const displayName = [form.firstname, form.lastname].filter(Boolean).join(" ") || form.email || "Manager";

  return (
    <div className="cp-wrap">
      {/* ── Page header ── */}
      <div className="cp-page-title">Profile Settings</div>
      <div className="cp-page-sub">Manage your personal information and account preferences</div>

      <div className="cp-layout">
        {/* ── Left: Avatar card ── */}
        <div>
          <AvatarPickerCard
            currentUrl={avatarUrl}
            displayName={displayName}
            backendBase={backendBase}
            onSaved={(url) => setAvatarUrl(url)}
            onRemoved={() => setAvatarUrl(null)}
          />
        </div>

        {/* ── Right: Info + Password ── */}
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          {/* Personal info */}
          <div className="cp-card">
            <div className="cp-card-head">
              <div>
                <div className="cp-card-title">Personal Information</div>
                <div className="cp-card-sub">Update your basic profile details</div>
              </div>
              <div className="cp-status-row">
                <div className="cp-status-dot" style={{ background: "#10b981" }} />
                <span style={{ fontSize: "12px", color: "#10b981", fontWeight: 500 }}>Complete</span>
              </div>
            </div>
            <div className="cp-card-body">
              <Alert message={msg} onClose={() => setMsg(null)} />

              <div className="cp-form-grid" style={{ marginBottom: "16px" }}>
                <Field label="First Name" required>
                  <input
                    className="cp-input"
                    placeholder="Enter your first name"
                    value={form.firstname}
                    onChange={(e) => setForm({ ...form, firstname: e.target.value })}
                  />
                </Field>
                <Field label="Last Name" required>
                  <input
                    className="cp-input"
                    placeholder="Enter your last name"
                    value={form.lastname}
                    onChange={(e) => setForm({ ...form, lastname: e.target.value })}
                  />
                </Field>
                <Field label="Email Address" required>
                  <input
                    className="cp-input"
                    type="email"
                    placeholder="Enter your email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                  />
                </Field>
                <Field label="Phone Number">
                  <PhoneInput
                    country={"us"}
                    value={form.phone}
                    onChange={handlePhoneChange}
                    inputProps={{ name: "phone", required: false }}
                    enableSearch={true}
                    disableSearchIcon={false}
                    autoFormat={true}
                    specialLabel=""
                    containerStyle={{ width: "100%" }}
                    inputStyle={{
                      width: "100%", height: "40px", fontSize: "14px",
                      paddingLeft: "60px", border: "1px solid #e2e8f0",
                      borderRadius: "7px", backgroundColor: "#fff",
                      fontFamily: "'DM Sans', sans-serif",
                      transition: "border-color .15s",
                    }}
                    buttonStyle={{
                      border: "1px solid #e2e8f0", borderRadius: "7px 0 0 7px",
                      backgroundColor: "#fff",
                    }}
                    dropdownStyle={{
                      backgroundColor: "#fff", border: "1px solid #e2e8f0",
                      borderRadius: "8px", boxShadow: "0 8px 24px rgba(0,0,0,.1)",
                    }}
                    searchStyle={{ padding: "8px 12px", border: "1px solid #e2e8f0", borderRadius: "6px", margin: "8px" }}
                  />
                </Field>
                <Field label="Country">
                  <input
                    className="cp-input"
                    placeholder="Enter your country"
                    value={form.country}
                    onChange={(e) => setForm({ ...form, country: e.target.value })}
                  />
                </Field>
                <Field label="Date of Birth">
                  <input
                    className="cp-input"
                    type="date"
                    value={form.birthday}
                    onChange={(e) => setForm({ ...form, birthday: e.target.value })}
                  />
                </Field>
              </div>

              {/* Pending email banner */}
              {pendingEmail && (
                <div className="cp-email-banner">
                  <div className="cp-email-banner-title">Email Verification Required</div>
                  <div className="cp-email-banner-body">
                    We've sent a verification code to <strong>{pendingEmail}</strong>. Please verify your new email address to complete the change.
                  </div>
                  <div className="cp-email-banner-btns">
                    <button className="cp-btn cp-btn-amber cp-btn-sm" onClick={confirmEmailChange}>
                      Enter Verification Code
                    </button>
                    <button className="cp-btn cp-btn-outline cp-btn-sm" onClick={resendEmailCode}>
                      Resend Code
                    </button>
                  </div>
                </div>
              )}

              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "20px" }}>
                <button className="cp-btn cp-btn-dark" onClick={save} disabled={saving}>
                  {saving && <div className="cp-spinner" />}
                  {saving ? "Saving…" : "Save Changes"}
                </button>
              </div>
            </div>
          </div>

          {/* Security / password */}
          <div className="cp-card">
            <div className="cp-card-head">
              <div>
                <div className="cp-card-title">Security Settings</div>
                <div className="cp-card-sub">Update your password and security preferences</div>
              </div>
              <div className="cp-status-row">
                <div className="cp-status-dot" style={{ background: passwordStrength.color || "#d1d5db" }} />
                <span style={{ fontSize: "12px", color: passwordStrength.color || "#9ca3af", fontWeight: 500 }}>
                  {pw.next ? passwordStrength.label : "Set password"}
                </span>
              </div>
            </div>
            <div className="cp-card-body">
              <Alert message={pwMsg} onClose={() => setPwMsg(null)} />

              <div className="cp-form-grid-3">
                {/* Current password */}
                <Field label="Current Password" required>
                  <div className="cp-input-wrap">
                    <input
                      className="cp-input"
                      type={showPasswords.current ? "text" : "password"}
                      placeholder="Current password"
                      value={pw.current}
                      onChange={(e) => setPw({ ...pw, current: e.target.value })}
                      style={{ paddingRight: "38px" }}
                    />
                    <button type="button" className="cp-input-eye" onClick={() => togglePasswordVisibility("current")}>
                      {showPasswords.current ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
                    </button>
                  </div>
                </Field>

                {/* New password */}
                <Field label="New Password" required>
                  <div className="cp-input-wrap">
                    <input
                      className="cp-input"
                      type={showPasswords.next ? "text" : "password"}
                      placeholder="Min. 8 characters"
                      value={pw.next}
                      onChange={(e) => setPw({ ...pw, next: e.target.value })}
                      style={{ paddingRight: "64px" }}
                    />
                    <button type="button" className="cp-input-gen" onClick={generatePassword} title="Generate secure password">
                      <BiRefresh size={17} />
                    </button>
                    <button type="button" className="cp-input-eye" onClick={() => togglePasswordVisibility("next")}>
                      {showPasswords.next ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
                    </button>
                  </div>
                  {pw.next && (
                    <>
                      <div className="cp-pw-bar-wrap">
                        <div className="cp-pw-bar" style={{ width: `${passwordStrength.pct}%`, background: passwordStrength.color }} />
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: "11px", marginTop: "4px" }}>
                        <span style={{ color: "#9ca3af" }}>Password strength</span>
                        <span style={{ color: passwordStrength.color, fontWeight: 500 }}>{passwordStrength.label}</span>
                      </div>
                    </>
                  )}
                </Field>

                {/* Confirm password */}
                <Field label="Confirm New Password" required>
                  <div className="cp-input-wrap">
                    <input
                      className="cp-input"
                      type={showPasswords.confirm ? "text" : "password"}
                      placeholder="Repeat new password"
                      value={pw.confirm}
                      onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
                      style={{ paddingRight: "38px" }}
                    />
                    <button type="button" className="cp-input-eye" onClick={() => togglePasswordVisibility("confirm")}>
                      {showPasswords.confirm ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
                    </button>
                  </div>
                  {pw.next && pw.confirm && pw.next !== pw.confirm && (
                    <div style={{ fontSize: "11px", color: "#ef4444", marginTop: "4px" }}>Passwords do not match</div>
                  )}
                </Field>
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "20px" }}>
                <button
                  className="cp-btn cp-btn-dark"
                  onClick={changePassword}
                  disabled={!pw.current || !pw.next || !pw.confirm || pw.next !== pw.confirm}
                >
                  Update Password
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Avatar picker card ───────────────────────────────────────── */
function AvatarPickerCard({ currentUrl, displayName, backendBase, onSaved, onRemoved }) {
  const [mode, setMode] = useState("idle");
  const [sourceUrl, setSourceUrl] = useState(null);
  const imgEl = useRef(null);
  const [disp, setDisp] = useState({ w: 0, h: 0, scale: 1, offsetX: 0, offsetY: 0 });
  const [box, setBox] = useState({ x: 0, y: 0, s: 0 });
  const dragRef = useRef({ active: false, ox: 0, oy: 0, sx: 0, sy: 0 });
  const resizeRef = useRef({ active: false, corner: null, ox: 0, oy: 0, s: 0, x: 0, y: 0 });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  function onPick(file) {
    if (!file) return;
    if (sourceUrl) URL.revokeObjectURL(sourceUrl);
    const url = URL.createObjectURL(file);
    setSourceUrl(url); setMode("editing"); setErr("");
  }
  function cancel() {
    if (sourceUrl) URL.revokeObjectURL(sourceUrl);
    setSourceUrl(null); setMode("idle"); setErr("");
  }
  function onImgLoad(e) {
    const img = e.target;
    const nw = img.naturalWidth, nh = img.naturalHeight;
    const scale = Math.min(AVATAR_VIEW / nw, AVATAR_VIEW / nh);
    const dw = Math.round(nw * scale), dh = Math.round(nh * scale);
    const offsetX = Math.floor((AVATAR_VIEW - dw) / 2), offsetY = Math.floor((AVATAR_VIEW - dh) / 2);
    setDisp({ w: dw, h: dh, scale, offsetX, offsetY });
    const s = Math.floor(Math.min(dw, dh) * 0.9);
    const x = offsetX + Math.floor((dw - s) / 2), y = offsetY + Math.floor((dh - s) / 2);
    setBox({ x, y, s });
  }
  function beginDrag(e) {
    e.preventDefault();
    dragRef.current = { active: true, ox: "touches" in e ? e.touches[0].clientX : e.clientX, oy: "touches" in e ? e.touches[0].clientY : e.clientY, sx: box.x, sy: box.y };
  }
  function beginResize(e, corner) {
    e.preventDefault();
    resizeRef.current = { active: true, corner, ox: "touches" in e ? e.touches[0].clientX : e.clientX, oy: "touches" in e ? e.touches[0].clientY : e.clientY, s: box.s, x: box.x, y: box.y };
  }
  useEffect(() => {
    function onMove(e) {
      const mx = "touches" in e ? e.touches[0].clientX : e.clientX;
      const my = "touches" in e ? e.touches[0].clientY : e.clientY;
      if (resizeRef.current.active) {
        const dx = mx - resizeRef.current.ox, dy = my - resizeRef.current.oy;
        const delta = Math.max(dx, dy);
        let newS = resizeRef.current.s, newX = resizeRef.current.x, newY = resizeRef.current.y;
        switch (resizeRef.current.corner) {
          case "tl": newS = Math.max(20, resizeRef.current.s - delta); newX = resizeRef.current.x + (resizeRef.current.s - newS); newY = resizeRef.current.y + (resizeRef.current.s - newS); break;
          case "tr": newS = Math.max(20, resizeRef.current.s + dx); newY = resizeRef.current.y + (resizeRef.current.s - newS); break;
          case "bl": newS = Math.max(20, resizeRef.current.s + dy); newX = resizeRef.current.x + (resizeRef.current.s - newS); break;
          case "br": newS = Math.max(20, resizeRef.current.s + delta); break;
        }
        const maxS = Math.min(disp.w, disp.h);
        newS = Math.min(newS, maxS);
        newX = Math.max(disp.offsetX, Math.min(disp.offsetX + disp.w - newS, newX));
        newY = Math.max(disp.offsetY, Math.min(disp.offsetY + disp.h - newS, newY));
        setBox({ x: newX, y: newY, s: newS });
        return;
      }
      if (!dragRef.current.active) return;
      const dx = mx - dragRef.current.ox, dy = my - dragRef.current.oy;
      const nx = dragRef.current.sx + dx, ny = dragRef.current.sy + dy;
      setBox({ ...box, x: Math.max(disp.offsetX, Math.min(disp.offsetX + disp.w - box.s, nx)), y: Math.max(disp.offsetY, Math.min(disp.offsetY + disp.h - box.s, ny)) });
    }
    function onUp() { dragRef.current.active = false; resizeRef.current.active = false; }
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", onUp);
    return () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); window.removeEventListener("touchmove", onMove); window.removeEventListener("touchend", onUp); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [box, disp]);

  async function confirm() {
    const img = imgEl.current;
    if (!img) return;
    setBusy(true); setErr("");
    try {
      const sx = Math.round((box.x - disp.offsetX) / disp.scale);
      const sy = Math.round((box.y - disp.offsetY) / disp.scale);
      const ss = Math.round(box.s / disp.scale);
      const cnv = document.createElement("canvas");
      cnv.width = OUT_W; cnv.height = OUT_H;
      const ctx = cnv.getContext("2d");
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(img, sx, sy, ss, ss, 0, 0, OUT_W, OUT_H);
      const blob = await new Promise((res) => cnv.toBlob(res, "image/png", 0.92));
      const fd = new FormData();
      fd.append("file", blob, "avatar.png");
      const r = await fetch(`${backendBase}/api/profile/avatar`, { method: "POST", credentials: "include", body: fd });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d?.message || "Upload failed");
      onSaved?.(d.avatarUrl);
      window.dispatchEvent(new CustomEvent("avatar:updated", { detail: d.avatarUrl }));
      cancel();
    } catch (e) {
      setErr(e.message || "Upload failed");
    } finally {
      setBusy(false);
    }
  }

  async function removeAvatar() {
    setBusy(true); setErr("");
    try {
      const r = await fetch(`${backendBase}/api/profile/avatar`, { method: "DELETE", credentials: "include" });
      if (!r.ok) throw new Error("Remove failed");
      onRemoved?.();
      window.dispatchEvent(new CustomEvent("avatar:updated", { detail: null }));
    } catch (e) {
      setErr(e.message || "Remove failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="cp-card">
      <div className="cp-card-head">
        <div>
          <div className="cp-card-title">Profile Picture</div>
          <div className="cp-card-sub">Upload a square image for best results</div>
        </div>
        <div className="cp-status-row">
          <div className="cp-status-dot" style={{ background: currentUrl ? "#10b981" : "#d1d5db" }} />
          <span style={{ fontSize: "12px", color: currentUrl ? "#10b981" : "#9ca3af", fontWeight: 500 }}>
            {currentUrl ? "HD Ready" : "No photo"}
          </span>
        </div>
      </div>
      <div className="cp-card-body">
        {mode === "idle" && (
          <>
            {/* Current avatar display */}
            <div className="cp-avatar-display">
              <div style={{ position: "relative", flexShrink: 0 }}>
                <img
                  src={currentUrl || "/assets/profile.png"}
                  alt="Avatar"
                  className="cp-avatar-img"
                />
                {currentUrl && (
                  <div className="cp-avatar-check">
                    <FaCheck size={9} />
                  </div>
                )}
              </div>
              <div>
                <div className="cp-avatar-name">{displayName}</div>
                <div className="cp-avatar-role">CEED MANAGER</div>
                <div className="cp-avatar-status">
                  {currentUrl ? "Profile picture is set" : "No profile picture"}
                </div>
                <div className="cp-avatar-actions">
                  <button
                    className="cp-btn cp-btn-dark cp-btn-sm"
                    onClick={() => document.getElementById("ceed-avatar-input")?.click()}
                  >
                    <FaCamera size={11} />
                    {currentUrl ? "Change" : "Upload"}
                  </button>
                  {currentUrl && (
                    <button className="cp-btn cp-btn-outline cp-btn-sm" disabled={busy} onClick={removeAvatar}>
                      <MdDelete size={13} />
                      {busy ? "Removing…" : "Remove"}
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Drop zone */}
            <IdleDropZone onPick={onPick} />

            {err && (
              <div className="cp-alert cp-alert-error" style={{ marginTop: "12px" }}>
                <ImCross size={12} style={{ flexShrink: 0 }} /> {err}
              </div>
            )}
          </>
        )}

        {mode === "editing" && (
          <>
            <div style={{ textAlign: "center", marginBottom: "14px" }}>
              <div style={{ fontSize: "13px", fontWeight: 500, color: "#374151" }}>Crop Your Picture</div>
              <div style={{ fontSize: "12px", color: "#9ca3af", marginTop: "3px" }}>Drag the selection to choose your crop area</div>
            </div>

            {/* Crop canvas */}
            <div className="cp-crop-wrap">
              <div
                style={{ position: "relative", width: AVATAR_VIEW, height: AVATAR_VIEW, background: "#f3f4f6", border: "1px solid #e8ecf0", borderRadius: "8px", overflow: "hidden" }}
                onDrop={(e) => { e.preventDefault(); onPick(e.dataTransfer?.files?.[0]); }}
                onDragOver={(e) => e.preventDefault()}
              >
                <img
                  ref={imgEl}
                  src={sourceUrl}
                  alt="Crop"
                  onLoad={onImgLoad}
                  draggable={false}
                  style={{ position: "absolute", left: disp.offsetX, top: disp.offsetY, width: disp.w || "auto", height: disp.h || "auto", userSelect: "none" }}
                />
                <div
                  onMouseDown={beginDrag}
                  onTouchStart={beginDrag}
                  style={{ position: "absolute", left: box.x, top: box.y, width: box.s, height: box.s, border: "2px solid #111111", cursor: "move", background: "rgba(0,0,0,0.08)", boxShadow: "0 0 0 9999px rgba(0,0,0,.55)" }}
                />
                {[["tl","nw",box.x-5,box.y-5],["tr","ne",box.x+box.s-5,box.y-5],["bl","sw",box.x-5,box.y+box.s-5],["br","se",box.x+box.s-5,box.y+box.s-5]].map(([c,cur,l,t]) => (
                  <div
                    key={c}
                    style={{ position: "absolute", left: l, top: t, width: 11, height: 11, borderRadius: "50%", background: "#111111", border: "2px solid #fff", cursor: `${cur}-resize` }}
                    onMouseDown={(e) => beginResize(e, c)}
                    onTouchStart={(e) => beginResize(e, c)}
                  />
                ))}
              </div>
            </div>

            {/* Preview */}
            <div className="cp-crop-preview" style={{ marginBottom: "16px" }}>
              <div className="cp-crop-preview-label">Preview</div>
              <div className="cp-crop-preview-circle">
                <div
                  style={{
                    width: "100%", height: "100%", backgroundImage: `url(${sourceUrl})`,
                    backgroundPosition: `-${((box.x - disp.offsetX) / disp.scale) * (72 / (box.s / disp.scale))}px -${((box.y - disp.offsetY) / disp.scale) * (72 / (box.s / disp.scale))}px`,
                    backgroundSize: `${(disp.w / disp.scale) * (72 / (box.s / disp.scale))}px ${(disp.h / disp.scale) * (72 / (box.s / disp.scale))}px`,
                  }}
                />
              </div>
            </div>

            {/* Buttons */}
            <div style={{ display: "flex", justifyContent: "center", gap: "10px" }}>
              <button className="cp-btn cp-btn-outline" onClick={cancel} disabled={busy}>Cancel</button>
              <button className="cp-btn cp-btn-dark" onClick={confirm} disabled={busy}>
                {busy && <div className="cp-spinner" />}
                {busy ? "Saving…" : "Save Picture"}
              </button>
            </div>

            {err && (
              <div className="cp-alert cp-alert-error" style={{ marginTop: "12px" }}>
                <ImCross size={12} style={{ flexShrink: 0 }} /> {err}
              </div>
            )}
          </>
        )}
      </div>

      {/* Hidden file input */}
      <input
        id="ceed-avatar-input"
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={(e) => onPick(e.target.files?.[0] || null)}
      />
    </div>
  );
}

/* ── Drop zone ────────────────────────────────────────────────── */
function IdleDropZone({ onPick }) {
  const inputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (e) => {
    e.preventDefault(); e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    else if (e.type === "dragleave") setDragActive(false);
  };
  const handleDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) onPick(e.dataTransfer.files[0]);
  };

  return (
    <div
      className={`cp-drop-zone ${dragActive ? "drag-active" : ""}`}
      onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop}
      onClick={() => inputRef.current?.click()}
    >
      <div style={{ fontSize: "28px", display: "flex", justifyContent: "center", color: "#9ca3af" }}>
        {dragActive ? <TbPhotoDown /> : <HiPhoto />}
      </div>
      <div className="cp-drop-title">{dragActive ? "Drop your image here" : "Drag & drop your image"}</div>
      <div className="cp-drop-sub">PNG, JPG, GIF up to 2 MB</div>
      <button
        className="cp-btn cp-btn-dark cp-btn-sm"
        style={{ marginTop: "12px" }}
        onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }}
      >
        Browse Files
      </button>
      <input ref={inputRef} type="file" accept="image/*" style={{ display: "none" }}
        onChange={(e) => onPick(e.target.files?.[0] || null)} />
    </div>
  );
}
